/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */
import '@babel/polyfill'; // Needed for redux-saga es6 generator support
import { LicenseInfo as MuiLicenseInfo } from '@mui/x-license-pro';
import {
  LicenseManager as AgGridLicenseManager,
  AllCommunityModule,
  ClipboardModule,
  ContextMenuModule,
  ExcelExportModule,
  ModuleRegistry,
  provideGlobalGridOptions,
} from 'ag-grid-enterprise';
import 'ag-grid-enterprise/styles/ag-grid.css'; // Core AG Grid CSS
import 'ag-grid-enterprise/styles/ag-theme-quartz.css'; // AG Grid theme
import { ConnectedRouter } from 'connected-react-router';
import React from 'react';
import { CookiesProvider } from 'react-cookie';
import ReactDOM from 'react-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { IntercomProvider } from 'react-use-intercom';

// Brand theme
import '@/styles/theme-kinetik.css';
import '@/styles/semantic-theme.css';
import '@/styles/primitive-colors.css';
import '@/styles/semantic-color-tokens.css';
import '@/styles/border-radii.css';
import '@/styles/spacing-scale.css';

import 'sanitize.css/sanitize.css';
import './ag-theme-kinetik.css'; // Custom AG Grid theme
import { LaunchDarklyProvider } from '@/components/common';
import CustomLocalizationProvider from 'components/CustomLocalizationProvider';
import { AlertProvider } from 'components/v2/Alerts/providers/AlertContext';
import App from 'containers/App'; // Root app
import LanguageProvider from 'containers/LanguageProvider';
import history from 'utils/history';

import configureStore from './configureStore';
import { translationMessages } from './i18n';

// Create redux store with history
const initialState = {};
const store = configureStore(initialState, history);
const MOUNT_NODE = document.getElementById('app');

MuiLicenseInfo.setLicenseKey(process.env.MUI_X_PRO_LICENSE_KEY);

AgGridLicenseManager.setLicenseKey(process.env.AG_GRID_ENTERPRISE_LICENSE_KEY);

// Register only the AG Grid modules actually used: all community features plus
// the two enterprise features in use — Excel export and context menu (both via
// getContextMenuItems, see utils/v2/getContextMenuItemsList.js). Avoids shipping
// the full ~2.3MB AllEnterpriseModule on every page load (TS-9341). Legacy
// theming keeps the existing CSS themes (ag-theme-quartz, ag-theme-kinetik).
ModuleRegistry.registerModules([
  AllCommunityModule,
  ClipboardModule,
  ExcelExportModule,
  ContextMenuModule,
]);
provideGlobalGridOptions({ theme: 'legacy' });

const render = (messages) => {
  ReactDOM.render(
    <ReduxProvider store={store}>
      <LaunchDarklyProvider>
        <CookiesProvider>
          <LanguageProvider messages={messages}>
            <CustomLocalizationProvider>
              <ConnectedRouter history={history}>
                <IntercomProvider
                  appId={process.env.INTERCOM_APP_ID}
                  apiBase={process.env.INTERCOM_API_BASE}
                >
                  <AlertProvider>
                    <App />
                  </AlertProvider>
                </IntercomProvider>
              </ConnectedRouter>
            </CustomLocalizationProvider>
          </LanguageProvider>
        </CookiesProvider>
      </LaunchDarklyProvider>
    </ReduxProvider>,
    MOUNT_NODE,
  );
};

// Vite HMR
if (import.meta.hot) {
  import.meta.hot.accept(['./i18n', 'containers/App'], () => {
    ReactDOM.unmountComponentAtNode(MOUNT_NODE);
    render(translationMessages);
  });
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  new Promise((resolve) => {
    resolve(import('intl'));
  })
    // eslint-disable-next-line import/extensions
    .then(() => Promise.all([import('intl/locale-data/jsonp/en.js')]))
    .then(() => render(translationMessages))
    .catch((err) => {
      throw err;
    });
} else {
  render(translationMessages);
}
